import TableToolbar from 'components/table/TableToolbar';
import React, { useEffect, useState } from 'react';
import MainTable from 'components/table/MainTable';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { theme } from 'utils/style/theme';
import SearchInput from 'components/search/SearchInput';

import { Image, Spin, message } from 'antd';
import NoImage from 'components/etc/NoImage';
import { Edit2, Filter, Trash } from 'iconsax-react';
import ActionButton from 'components/table/ActionButton';
import { LoadingOutlined } from '@ant-design/icons';
import { useCookies } from 'react-cookie';
import { handleMessage } from 'utils/helper/message';
import { useParams } from 'react-router';
import DefaultButton from 'components/button/DefaultButton';
import CreateModal from './Modal/CreateModal';
import { useNavigate, useLocation } from 'react-router-dom';
import { itemApis } from 'apis/package/itemApis';
import CheckboxStyled from 'components/CheckboxStyled';
import { queryLocalizedCodeList } from 'apis/common/commonApis';

import ColumnItem from './Modal/ItemTypeFilterModal/ColumnItem';
import DeleteModal from 'components/modal/DeleteModal';

import { EmptyItemExtension, IItem, IProject } from 'utils/types/storyTypes';
/**
 * * 아이템 - 목록 2023.12.12
 */
interface IProps {
  currentProject: IProject;
}

export interface IVisible {
  create: boolean;
  uploading: boolean;
  edit: boolean;
  delete: boolean;
}

const ItemPage: React.FC<IProps> = ({ currentProject }: IProps) => {
  const { t } = useTranslation();
  const [cookies] = useCookies();

  const { projectId } = useParams();

  const navigate = useNavigate();

  // 팝업 visible 제어
  const [visibleState, setVisibleState] = useState<IVisible>({
    create: false,
    uploading: false,
    edit: false,
    delete: false,
  });

  // 아이템 type 기준정보
  const { isLoading: isLoadingItemType, data: dropdownItemType } =
    queryLocalizedCodeList('item_type', cookies.lang);

  const [searchData, setSearchData] = useState<string>(''); // 이름 검색어

  const [itemFilter, setItemFilter] = useState<string[]>([]); // 아이템 필터 (검색용)

  const [itemList, setItemList] = useState<IItem[]>([]); // 원 데이터
  const [tableData, setTableData] = useState<IItem[]>([]); // 테이블 소스

  const [selectedData, setSelectedData] = useState<any>(null);

  const [refresh, setRefresh] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const antLoadingIcon = <LoadingOutlined style={{ fontSize: 50 }} spin />;

  // * 아이템 리스트 요청
  const requestItemList = async () => {
    if (projectId) {
      try {
        setLoading(true);

        // 프로젝트 아이템 목록 가져오기
        const itemRes = await itemApis.itemList(projectId);
        setItemList(itemRes.list);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.log('fetch data error: ', err);
      }
    } else {
      console.log(`Item. No projoectId`);
    }
  };

  useEffect(() => {
    setTableData(itemList);
  }, [itemList]);

  useEffect(() => {
    requestItemList();
  }, [refresh]);

  // 아이템 수정화면으로 이동
  const onClickItemUpdate = (record: IItem) => {
    console.log(`onClickItemUpdate `);

    if (!record.extension) {
      record.extension = EmptyItemExtension;
    }

    navigate(`${record.id}/update`, {
      state: {
        currentProject: currentProject,
        currentItem: record,
      },
    });
  };

  // 검색어 및 필터링에 대한 처리
  useEffect(() => {
    if (itemList.length == 0) return;
    console.log(`search start :: `, itemList);

    let filteredData = structuredClone(itemList);

    if (searchData && searchData.length > 0) {
      console.log(`has searchData : `, searchData);
      filteredData = filteredData.filter((e) =>
        e.origin_name.toLowerCase().includes(searchData.toLowerCase()),
      );
    }

    if (itemFilter && itemFilter.length > 0) {
      console.log(`Filter item type : `, itemFilter);

      filteredData = filteredData.filter((item) => {
        return itemFilter.includes(item.item_type);
      });
    }

    console.log(`after search filter data :: `, filteredData);
    setTableData(filteredData);
  }, [searchData, itemFilter, refresh]);

  // * 삭제 컨펌 물어보기
  const handleClickDeleteModal = (record) => {
    console.log(`handleClickDeleteModal : `, record);

    setSelectedData(record);
    setVisibleState({
      ...visibleState,
      delete: true,
    });
  };

  // * 실제 삭제 처리
  const handleDelete = async () => {
    if (selectedData) {
      try {
        setLoading(true);

        await itemApis.itemDelete(projectId, selectedData.id);
        setRefresh(!refresh);
        setLoading(false);
        setVisibleState({
          ...visibleState,
          delete: false,
        });
      } catch (err) {
        setLoading(false);
        handleMessage(message, 'error', 'Please try again');
        console.log('Delete error:', err);
      }
    }
  };

  const tableActionMenu = (record) => {
    return [
      {
        key: 1,
        item: (
          <Edit2
            size='24'
            onClick={() => {
              setSelectedData(record);
              onClickItemUpdate(record);
            }}
          />
        ),
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },

      {
        key: 2,
        item: (
          <Trash
            size='24'
            onClick={() => {
              handleClickDeleteModal(record);
            }}
          />
        ),
        hoverColor: theme.colors.red_500,
        color: theme.colors.dark_200,
      },
    ];
  };

  // * 테이블 컬럼
  const columnsOrigin = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 'fit-content',
      align: 'center',
    },
    {
      title: t('storyEpisodeID.storyTabMenuList.operationContent.table.name'),
      dataIndex: 'origin_name',
      width: '3rem',
    },
    {
      title: (
        <ColumnItem
          filterList={dropdownItemType}
          setItemFilter={setItemFilter}
          itemFilter={itemFilter}
        />
      ),
      dataIndex: 'item_type',
      width: '3rem',
      align: 'center',
    },
    {
      title: '아이콘(메일함)',
      dataIndex: 'icon_url',
      width: '3rem',
      align: 'center',
      render: (_, record) => (
        <IDColumnWrapper>
          {_ === null || _ === '' ? (
            <NoImage width='40px' height='40px' />
          ) : (
            <Image
              src={_}
              alt={record.image_name}
              height={40}
              width={40}
              style={{ objectFit: 'cover' }}
            />
          )}
        </IDColumnWrapper>
      ),
    },
    {
      title: 'UI 이미지',
      dataIndex: 'image_url',
      width: '3rem',
      align: 'center',
      render: (_, record) => (
        <IDColumnWrapper>
          {_ === null || _ === '' ? (
            <NoImage width='40px' height='40px' />
          ) : (
            <Image
              src={_}
              alt={record.image_name}
              height={40}
              width={40}
              style={{ objectFit: 'cover' }}
            />
          )}
        </IDColumnWrapper>
      ),
    },
    {
      title: '주 재화(하트,보석,루비)',
      dataIndex: 'is_prime',
      width: '6rem',
      align: 'center',
      render: (_) => {
        return (
          <div className='flex-center'>
            <CheckboxStyled isChecked={_} />
          </div>
        );
      },
    },
    {
      dataIndex: '',
      width: '8rem',
      render: (text, record) => {
        const menus = [tableActionMenu(record)];
        return <ActionButton menus={menus} />;
      },
    },
  ];

  return (
    <>
      <TableToolbar>
        <SearchInput placeholder='Search...' setSearchData={setSearchData} />
        <div>
          <DefaultButton
            name='create'
            onClick={() => setVisibleState({ ...visibleState, create: true })}
          >
            {t(
              'storyEpisodeID.storyTabMenuList.operationContent.header.button.create',
            )}
          </DefaultButton>
        </div>
      </TableToolbar>
      {loading ? (
        <LoadingWrapper>
          <Spin indicator={antLoadingIcon} />
        </LoadingWrapper>
      ) : (
        <MainTable
          columns={columnsOrigin}
          dataSource={tableData}
          isPagination={false}
        />
      )}
      <CreateModal
        visible={visibleState}
        setVisible={setVisibleState}
        refresh={refresh}
        setRefresh={setRefresh}
        itemType={dropdownItemType}
      />
      <DeleteModal
        visible={visibleState.delete}
        setVisible={(e) => {
          setVisibleState({ ...visibleState, delete: e });
        }}
        title={t(
          'storyEpisodeID.storyTabMenuList.operationContent.modal.delete.title',
        )}
        content={t(
          'storyEpisodeID.storyTabMenuList.operationContent.modal.delete.content',
        )}
        label={selectedData?.origin_name}
        handleDelete={handleDelete}
        loading={loading}
      />
    </>
  );
};

export default ItemPage;

const IDColumnWrapper = styled.div`
  display: flex;
  column-gap: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
